import PropTypes from 'prop-types'
import { FacilityPageQuery, FACILITY_QUERY } from '@layouts/facility'
import initializeFeatures from '@lib/feature-flags/initializeFeatures'
import { isGolfFacilitiesDataSourceEnabled } from '@lib/feature-flags/facility-rollout'
import { fetchSSRData } from '@lib/ssr-fetch'
import { withGolfFacilitiesSsr } from '@lib/golf-facilities/client'
import { adaptFacilityDetailToFacilityPageProps } from '@lib/golf-facilities/adapters/facility-detail'
import { firstQueryParam } from '@utils/firstQueryParam'
import { convertToSlug } from '@utils/slugs'

const PLAY_DETAIL_LOG_PREFIX = '[play/detail]'

function FacilityPage({ query, ssrData }) {
  return (
    <FacilityPageQuery {...query} ssrData={ssrData} />
  )
}

function buildFacilityDetailPath(detail) {
  const address = detail?.address || {}
  const state = String(address.region ?? '').toLowerCase()
  const city = convertToSlug(address.city ?? '')
  const course = detail?.slug ?? convertToSlug(detail?.name ?? '')
  const publicId = detail?.publicId ? String(detail.publicId) : ''
  return `/play/${state}/${city}/${course}/${publicId}`.toLowerCase()
}

function buildRequestedFacilityPath(query) {
  const state = firstQueryParam(query?.state) ?? ''
  const city = firstQueryParam(query?.city) ?? ''
  const course = firstQueryParam(query?.course) ?? ''
  const id = firstQueryParam(query?.id) ?? ''
  return `/play/${state}/${city}/${course}/${id}`.toLowerCase()
}

async function loadLegacyFacilityProps(query) {
  const result = await fetchSSRData({
    query,
    graphqlQuery: FACILITY_QUERY,
    variables: { id: query.id },
    clientName: 'pga-algolia',
    transform: (data) => data?.facility,
  })
  return { props: { query, ssrData: result.data ?? null } }
}

export async function getServerSideProps({ req, res, query }) {
  const safeQuery = query ?? {}
  res.setHeader('Cache-Control', 'no-store')
  const featureFlags = await initializeFeatures({ req, res, query: safeQuery }).catch(() => null)

  if (!isGolfFacilitiesDataSourceEnabled(featureFlags)) {
    return loadLegacyFacilityProps(safeQuery)
  }

  const facilityId = firstQueryParam(safeQuery.id)
  if (!facilityId) return { notFound: true }

  const outcome = await withGolfFacilitiesSsr(
    (client) => client.facilities.get(String(facilityId)),
    {
      logPrefix: PLAY_DETAIL_LOG_PREFIX,
      notFoundContext: {
        facilityId,
        idKind: /^\d+$/.test(facilityId) ? 'legacyNumeric' : 'publicId',
        path: buildRequestedFacilityPath(safeQuery),
      },
    },
  )
  if (outcome.notFound) return { notFound: true }

  const publicId = outcome.data?.publicId ? String(outcome.data.publicId) : ''
  if (publicId && String(facilityId) !== publicId) {
    return {
      redirect: {
        destination: buildFacilityDetailPath(outcome.data),
        statusCode: 301,
      },
    }
  }

  return {
    props: {
      query: safeQuery,
      ssrData: adaptFacilityDetailToFacilityPageProps(outcome.data),
    },
  }
}

FacilityPage.propTypes = {
  query: PropTypes.object,
  ssrData: PropTypes.object,
}

export default FacilityPage
